import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { UnitData } from '@services/requests/searchLoaders/unitLoader/UnitLoaderQuery';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadedPallet } from '../../../types';
import { EditPalletParametersDialog } from './EditPalletParametersDialog';
import { PalletParametersItem } from './PalletParametersItem';
import { PalletToEditState } from './types';

interface CrossDockingPalletsParametersProps {
  isActionsDisabled: boolean;
  availablePalletUnits: UnitData[];
  withPallets: boolean;
  palletsLoaded: LoadedPallet[];
  Position: number;
  cargoType: 'conainer' | 'truck';
  isCrossDockingEnabled: boolean;
  onGenerateLocalizedName: (defaultName: string, localizations: LocalizedMessage[]) => string;
  onChangeValueByProperty: (property: string, value: unknown) => void;
}

function CrossDockingPalletsParameters({
  isActionsDisabled,
  withPallets,
  palletsLoaded,
  Position,
  availablePalletUnits,
  cargoType,
  isCrossDockingEnabled,
  onChangeValueByProperty,
  onGenerateLocalizedName,
}: CrossDockingPalletsParametersProps) {
  const [palletToEdit, setPalletToEdit] = useState<PalletToEditState>(null);

  const { t } = useTranslation('Common');

  const isNoPalletsAvailable = availablePalletUnits.length === 0;

  /**
   * Добавление новой паллеты
   */
  const addNewPalletHandler = () => {
    if (availablePalletUnits.length === 0) {
      return;
    }

    const newPallet: LoadedPallet = {
      type: availablePalletUnits[0].id,
      quantity: 1,
      weight: 10000,
    };

    onChangeValueByProperty('palletsLoaded', [...palletsLoaded, newPallet]);
  };

  /**
   * Удаление паллеты
   */
  const deletePalletHandler = (palletIndextoDelete: number) => {
    onChangeValueByProperty(
      'palletsLoaded',
      palletsLoaded.filter((_, index) => index !== palletIndextoDelete)
    );
  };

  /**
   * Сохранение изменений паллеты
   */
  const savePalletHandler = (newPallet: PalletToEditState) => {
    if (!newPallet) {
      return;
    }
    const { index: _, ...restNewPallet } = newPallet;
    onChangeValueByProperty(
      'palletsLoaded',
      palletsLoaded.map((pallet, index) => (index === newPallet.index ? restNewPallet : pallet))
    );
  };

  /**
   * Открыть редактирование паллеты
   */
  const setPalletToEditHandler = (palletToEdit: LoadedPallet, index: number) => {
    setPalletToEdit({ ...palletToEdit, index });
  };

  /**
   * Закрыть редактирование паллеты
   */
  const closePalletEditingHandler = () => {
    setPalletToEdit(null);
  };

  if (!isCrossDockingEnabled) {
    return null;
  }

  return (
    <>
      <div className="pallets-block">
        <FormControlLabel
          className="base-params__item"
          label={
            <span>
              {cargoType === 'conainer'
                ? t('Common:PalletsExistingForContainer')
                : t('Common:PalletsExistingForTruck')}
            </span>
          }
          control={
            <Checkbox
              size={'small'}
              checked={withPallets}
              disabled={isActionsDisabled}
              onChange={(_, newValue) => onChangeValueByProperty('withPallets', newValue)}
            />
          }
        />
        {withPallets && isNoPalletsAvailable && (
          <div className="pallets-wrap">
            <span className="no-pallets-text">
              {cargoType === 'conainer'
                ? t('Common:NoAvailablePalletsForContainer')
                : t('Common:NoAvailablePalletsForTruck')}
            </span>
          </div>
        )}
        {withPallets && !isNoPalletsAvailable && (
          <div className="pallets-wrap">
            <IconButton
              title={`+ ${t('Actions:Add')}`}
              className={'add-new-pallet'}
              disabled={isActionsDisabled}
              onClick={() => addNewPalletHandler()}
            >
              <span className="add-new-pallet__text">{t('Common:AddPallet')}</span>
            </IconButton>
            <div className="pallet-items">
              {palletsLoaded.map((pallet, index) => (
                <PalletParametersItem
                  key={`container-${Position}-pallet-${index}`}
                  availablePalletUnits={availablePalletUnits}
                  isActionsDisabled={isActionsDisabled}
                  palletIndex={index}
                  palletData={pallet}
                  onGenerateLocalizedName={onGenerateLocalizedName}
                  onDeletePallet={() => deletePalletHandler(index)}
                  onEditPallet={() => setPalletToEditHandler(pallet, index)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <EditPalletParametersDialog
        availablePalletUnits={availablePalletUnits}
        palletToEdit={palletToEdit}
        onSave={savePalletHandler}
        onGenerateLocalizedName={onGenerateLocalizedName}
        onClose={closePalletEditingHandler}
      />
    </>
  );
}

export { CrossDockingPalletsParameters };
